import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createRequirement } from '../../store/slices/hrSlice';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { HR_REQUIREMENT_STATUS } from '../../utils/constants';

const RequirementForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hrEmployees, setHrEmployees] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    department: '',
    experience_required: '',
    number_of_openings: 1,
    assigned_hr: user?._id ? [user._id] : [],
    status: HR_REQUIREMENT_STATUS.OPEN
  });

  useEffect(() => {
    // Fetch HR employees for assignment
    const fetchHREmployees = async () => {
      try {
        const response = await api.get('/employees/by-department?department=hr');
        setHrEmployees(response.data.data);
      } catch (err) {
        console.error('Failed to fetch HR employees:', err);
        // Fallback to current user if fetch fails
        setHrEmployees([{ _id: user._id, name: user.name }]);
      }
    };
    fetchHREmployees();
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (name === 'assigned_hr') {
      // Handle multi-select for assigned HR
      const options = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({
        ...formData,
        assigned_hr: options
      });
    } else {
      const val = type === 'number' ? parseInt(value) : value;
      setFormData({
        ...formData,
        [name]: val
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await dispatch(createRequirement(formData)).unwrap();
      navigate(`/hr/requirements/${result._id}`);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container-fluid" style={{ maxWidth: '800px' }}>
        <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/hr')}>
          <i className="ti ti-arrow-left me-1" />
          Back to Dashboard
        </button>

        <div className="card">
          <div className="card-body">
            <h2 className="fs-5 mb-3">Create New Requirement</h2>

            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Job Title *</label>
                <input
                  type="text"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Senior Software Engineer"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Department *</label>
                <input
                  type="text"
                  name="department"
                  className="form-control"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="e.g., Engineering, Marketing, Finance"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Experience Required *</label>
                <input
                  type="text"
                  name="experience_required"
                  className="form-control"
                  value={formData.experience_required}
                  onChange={handleChange}
                  placeholder="e.g., 3-5 years"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Number of Openings *</label>
                <input
                  type="number"
                  name="number_of_openings"
                  className="form-control"
                  value={formData.number_of_openings}
                  onChange={handleChange}
                  min="1"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Assigned HR * (Hold Ctrl/Cmd to select multiple)</label>
                <select
                  name="assigned_hr"
                  className="form-select"
                  value={formData.assigned_hr}
                  onChange={handleChange}
                  multiple
                  required
                  style={{ minHeight: '120px' }}
                >
                  {hrEmployees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name} ({emp.employee_code})
                    </option>
                  ))}
                </select>
                <small className="text-muted d-block mt-1">
                  Selected: {formData.assigned_hr.length > 0
                    ? hrEmployees.filter(emp => formData.assigned_hr.includes(emp._id)).map(emp => emp.name).join(', ')
                    : 'None'}
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Status *</label>
                <select
                  name="status"
                  className="form-select"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  {Object.values(HR_REQUIREMENT_STATUS).map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/hr')}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Requirement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RequirementForm;
